import { Moment } from 'moment/moment';
import { GridColDef } from '@mui/x-data-grid';
import Typography from '@mui/material/Typography';
import startCase from 'lodash/startCase';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { getDatesRange } from '@mgm/common/utils';
import { useGetMgmReportAPI } from '../hooks/useMgmApi';
import { IntegrationExportTable } from '../../Shared/components';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getMgmReportData } from '../../../../store/payroll/selectors';
import { useGetOrderFirstPageAPI, useGetOrdersMappingAPI } from '../../../orders/Shared/hooks';
import { useFetchRemainingOrders } from '../../../orders/Orders/useFetchRemainingOrders';
import { getCommissionsSummary } from '../../../../store/commissions/selectors';
import { getCurrencyByCode } from '../../../../store/common/selectors';
import { TGetOrdersRes } from '../../../../types/responses/orders.type';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';
import { useGetOrdersCountAPI } from '../../../../hooks/api/useOrders';
import { TOrderMapping } from '../../../../types';

const MgmExportTable = () => {
  const { mutate, isLoading } = useGetMgmReportAPI();
  const mgmReports = useAppSelector(getMgmReportData);
  const { t } = useTranslation(['inputs', 'common', 'timesheets', 'payroll']);
  const { fromDate, toDate } = getDatesRange();
  const getCurrency = useAppSelector(getCurrencyByCode);
  const { shop } = useAppSelector(getAuthenticateUser);
  const commissionSummary = useAppSelector(getCommissionsSummary);

  const [isRefetching, setIsRefetching] = useState(false); // State to control rendering logic
  const firstPageDataRef = useRef<TGetOrdersRes | null>(null); // Ref to track firstPageData without re-renders

  const [dates, setDates] = useState<[Moment, Moment]>([
    moment.tz(fromDate, shop.shopTimezone),
    moment.tz(toDate, shop.shopTimezone),
  ]);

  const { data: orderMappingsRes } = useGetOrdersMappingAPI() as {
    data: {
      ordersMapping: Array<TOrderMapping>
    }
  };

  const { data, isFetching, refetch } = useGetOrderFirstPageAPI({
    isMainOrdersPage: false,
    dates,
  });

  const { refetch: refetchCount } = useGetOrdersCountAPI({
    isMainPage: false,
    dates,
  });

  const { refetch: refetchRemainingOrders } = useFetchRemainingOrders(data as TGetOrdersRes, orderMappingsRes?.ordersMapping);

  useEffect(() => {
    if (firstPageDataRef.current?.hasNext && !isRefetching) {
      setIsRefetching(true); // Prevent multiple refetch calls
      refetchRemainingOrders().finally(() => {
        setIsRefetching(false);
      }); // Reset refetch state
    }
  }, [isRefetching, refetchRemainingOrders]);

  const columns: Array<GridColDef> = useMemo(() => [
    {
      field: 'first_name',
      headerName: t('name'),
      minWidth: 130,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {startCase(row.first_name)}
          {' '}
          {startCase(row.last_name)}
        </Typography>
      ),
      flex: 1,
      editable: true,
    },
    {
      field: 'email',
      headerName: t('email'),
      minWidth: 200,
      flex: 1,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.email || '/'}
        </Typography>
      ),
    },
    {
      field: 'title',
      headerName: t('position'),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.title || '/'}
        </Typography>
      ),
    },
    {
      field: 'payment_type',
      headerName: t('paymentType'),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.payment_type || '/'}
        </Typography>
      ),
    },
    {
      field: 'payment_amount',
      headerName: t('paymentAmount'),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.payment_amount || '/'}
        </Typography>
      ),
    },
    {
      field: 'shifts_count',
      headerName: t('shifts', { ns: 'common' }),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.shifts_count || '/'}
        </Typography>
      ),
    },
    {
      field: 'hours_component',
      headerName: t('hours'),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.hours_component || 0}
        </Typography>
      ),
    },
    {
      field: 'minutes_component',
      headerName: t('minutes', { ns: 'payroll' }),
      minWidth: 80,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.minutes_component || 0}
        </Typography>
      ),
    },
    {
      field: 'hours_total',
      headerName: t('totalHours', { ns: 'timesheets' }),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.hours_total || 0}
        </Typography>
      ),
    },
    {
      field: 'breaks_count',
      headerName: t('breaksCount', { ns: 'payroll' }),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.breaks_count || 0}
        </Typography>
      ),
    },
    {
      field: 'breaks_total',
      headerName: t('totalBreaks', { ns: 'payroll' }),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.breaks_total || 0}
        </Typography>
      ),
    },
    {
      field: 'commission',
      headerName: t('commission', { ns: 'payroll' }),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => {
        const summary = commissionSummary.find(
          (u) => String(u.user.email) === String(row.email),
        );

        return (
          <Typography variant="caption">
            {`${(summary?.totalCommissions || 0)?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} ${getCurrency().mark}` || 0}
          </Typography>
        );
      },
      valueGetter: (_, row) => {
        const summary = commissionSummary.find(
          (u) => String(u.user.email) === String(row.email),
        );

        return `${(summary?.totalCommissions || 0)?.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} ${getCurrency().mark}` || 0;
      },
    },
    {
      field: 'PTO_other',
      headerName: 'PTO other',
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.PTO_other || 0}
        </Typography>
      ),
    },
    {
      field: 'PTO_vacation',
      headerName: 'PTO vacation',
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.PTO_vacation || 0}
        </Typography>
      ),
    },
    {
      field: 'timeoff_PTO_total',
      headerName: 'Timeoff PTO total',
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.timeoff_PTO_total || 0}
        </Typography>
      ),
    },
    {
      field: 'timeoff_sick_leave',
      headerName: t('timeoffSickLeave', { ns: 'payroll' }),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.timeoff_sick_leave || 0}
        </Typography>
      ),
    },
    {
      field: 'timeoff_total',
      headerName: t('totalTimeoff', { ns: 'payroll' }),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.timeoff_total || 0}
        </Typography>
      ),
    },
    {
      field: 'total_pay',
      headerName: t('totalPay', { ns: 'payroll' }),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {`${(row.total_pay || 0)?.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} ${getCurrency().mark}` || 0}
        </Typography>
      ),
    },
    {
      field: 'personal_note',
      headerName: t('personalNote', { ns: 'payroll' }),
      minWidth: 130,
      flex: 1,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.personal_note || '/'}
        </Typography>
      ),
      editable: true,
    },
  ], [commissionSummary]);

  const getReport = async (emails: Array<string>, dates: [Moment, Moment]) => {
    const data = {
      emails,
      dates,
    };

    const { data: firstPageData } = await refetch();
    await refetchCount();
    if (firstPageData && 'nextLink' in firstPageData) {
      if (firstPageDataRef.current?.nextLink !== firstPageData?.nextLink) {
        firstPageDataRef.current = firstPageData; // Update ref without triggering re-renders
      }
    }

    setIsRefetching(true); // This will trigger the useEffect to refetch remaining orders

    mutate(data);
  };

  return (
    <IntegrationExportTable
      getReport={getReport}
      isLoading={isLoading || isFetching}
      columns={columns}
      integrationName="ManageMate"
      rowsData={mgmReports}
      setDates={setDates}
      dates={dates}
    />
  );
};

export {
  MgmExportTable,
};
